import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import { EBENE_TOAST } from './ebenen';
import { fehlerOhneInhalt, melde } from '@/lib/nutzung';

type ToastKind = 'success' | 'error';
type Toast = { id: number; kind: ToastKind; message: string };

const Ctx = createContext<((kind: ToastKind, message: string) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const showToast = useCallback((kind: ToastKind, message: string) => {
    setToast({ id: Date.now(), kind, message });
    // Nutzungsmessung: welche Fehler sehen wir tatsächlich? Ohne Inhalt —
    // Titel, Namen und Nummern fallen vorher heraus.
    if (kind === 'error') melde('fehler_gezeigt', { ort: fehlerOhneInhalt(message) });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);
  return (
    <Ctx.Provider value={showToast}>
      {children}
      {toast && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
          className={clsx(
            // Ueber allem, siehe lib/ebenen.ts. Frueher z-[60] und damit
            // unter dem Scan-Fenster — der Wareneingang-Modus meldet eine
            // MISSLUNGENE Buchung nur ueber genau diesen Toast, und bei
            // laufender Kamera waere er unsichtbar gewesen. (Den
            // Erfolgsfall meldet seit A25 das Kurzjournal im Fenster.)
            //
            // `print:hidden` (Befund 29.10.2026): Der Toast hängt INNERHALB
            // von #root, nicht als Portal am <body> — die Druckregeln, die
            // Portale abräumen, erreichen ihn nicht. „Position gespeichert"
            // stand deshalb mit auf dem Lieferschein.
            `fixed top-4 right-4 ${EBENE_TOAST} max-w-md rounded-md border bg-cream px-4 py-3 text-sm shadow-sm print:hidden`,
            toast.kind === 'error' ? 'border-coral text-coral' : 'border-teal text-ink'
          )}
        >
          {toast.message}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast ausserhalb ToastProvider');
  return ctx;
}
